import * as React from "react";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { ITableHeader, tablesConfig, TableTypes } from "./tablesConfig";
import styles from "./Table.module.scss";
import { useTranslation } from "next-i18next";

interface ITableProps<T = unknown> {
    type: TableTypes;
    data: T[];
    renderRowHoverComponent?: (rowData: T) => React.ReactElement;
}

const TableValueRenderer = ({
    rowData,
    columnConfig,
}: {
    rowData: unknown;
    columnConfig: ITableHeader;
}) => {
    return columnConfig.renderValue(rowData);
};

const CustomTableHeader = ({
    headers,
    translate,
}: {
    headers: ITableHeader[];
    translate: (key: string, options?: Record<string, unknown>) => string;
}) => (
    <TableHead className={styles.tableHead}>
        <TableRow>
            {headers.map((header, index) => (
                <TableCell key={index} align={header.align || "left"}>
                    {translate(header.translationKey || header.label, { defaultValue: header.label })}
                </TableCell>
            ))}
        </TableRow>
    </TableHead>
);

const TableRowTooltip = ({
    children,
    tooltipContent,
}: {
    children: React.ReactElement;
    tooltipContent: string | React.ReactNode;
}) => {
    return (
        <Tooltip
            slotProps={{
                tooltip: {
                    sx: {
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                    },
                },
            }}
            title={tooltipContent}>
            {children}
        </Tooltip>
    );
};

const CustomTableBody = ({
    rows,
    columnsConfig,
    renderRowHoverComponent,
}: {
    rows: unknown[];
    columnsConfig: ITableHeader[];
    renderRowHoverComponent?: (rowData: unknown) => React.ReactElement;
}) => {
    return (
        <TableBody className={styles.tableBody}>
            {rows?.map((row, rowIndex) => (
                <TableRow
                    hover
                    className={styles.tableRow}
                    key={`row-${rowIndex}`}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    {columnsConfig.map((columnConfig, cellIndex) => (
                        <TableRowTooltip
                            key={`tooltip-${rowIndex}-${cellIndex}`}
                            tooltipContent={
                                columnConfig.withToolTip ? renderRowHoverComponent?.(row) || "" : ""
                            }>
                            <TableCell
                                className={styles.tableCell}
                                align={columnConfig.align || "left"}
                                key={`cell-${rowIndex}-${cellIndex}`}>
                                <TableValueRenderer rowData={row} columnConfig={columnConfig} />
                            </TableCell>
                        </TableRowTooltip>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
};

export const CustomTable = <T = unknown,>({ type, data, renderRowHoverComponent }: ITableProps<T>) => {
    const config = tablesConfig[type];
    const { t } = useTranslation("common");
    return (
        <TableContainer sx={{ boxShadow: "none" }} component={Paper} className={styles.tableContainer}>
            <Table size="small" aria-label={`${config.title} || 'table'`}>
                <CustomTableHeader headers={config.columns} translate={t} />
                <CustomTableBody
                    rows={data}
                    columnsConfig={config.columns}
                    renderRowHoverComponent={renderRowHoverComponent}
                />
            </Table>
        </TableContainer>
    );
};
